type Waiting = { resolve: (value: Record<string, boolean>) => void; reject: (reason: unknown) => void };
const waiting = new Map<string, Waiting[]>();
const cache = new Map<string, { until: number; value: Record<string, boolean> }>();
const inFlight = new Map<string, Promise<Record<string, boolean>>>();
let timer: ReturnType<typeof setTimeout> | undefined;

/** Related cards mount together: one public request for their unique IDs. */
export function loadHostActivity(carId: string): Promise<Record<string, boolean>> {
  const hit = cache.get(carId);
  if (hit && hit.until > Date.now()) return Promise.resolve(hit.value);
  const running = inFlight.get(carId);
  if (running) return running;
  const promise = new Promise<Record<string, boolean>>((resolve, reject) => {
    waiting.set(carId, [...(waiting.get(carId) ?? []), { resolve, reject }]);
    if (timer) return;
    timer = setTimeout(async () => {
      timer = undefined;
      const batch = new Map(waiting);
      waiting.clear();
      for (const [id, entry] of cache) if (entry.until <= Date.now()) cache.delete(id);
      try {
        const entries = [...batch.keys()];
        const result: Record<string, boolean> = {};
        // Preserve the endpoint's existing 100-ID bound on large search pages.
        for (let offset = 0; offset < entries.length; offset += 100) {
          const ids = entries.slice(offset, offset + 100).join(",");
          const response = await fetch(`${import.meta.env.BASE_URL}api/cars/host-activity-status?ids=${encodeURIComponent(ids)}`);
          if (!response.ok) throw new Error(response.statusText || String(response.status));
          Object.assign(result, await response.json());
        }
        for (const [id, subscribers] of batch) {
          cache.set(id, { until: Date.now() + 5 * 60_000, value: result });
          for (const subscriber of subscribers) subscriber.resolve(result);
        }
      } catch (error) {
        for (const subscribers of batch.values()) for (const subscriber of subscribers) subscriber.reject(error);
      }
    // Non-blocking badges can wait for SSR cards and refreshed related cards
    // to mount together, instead of sending a second initial-page batch.
    }, 750);
  });
  inFlight.set(carId, promise);
  void promise.then(() => inFlight.delete(carId), () => inFlight.delete(carId));
  return promise;
}
